import { FunctionComponent, PropsWithChildren, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { updateUserDefaultUnitsIfNeeded } from '@vestico/model';

import { UserContext } from '@vestico/shared';

import { MatchingAPIContext, WidgetAuthContext } from './MainContext';
import { TranslationsContext } from './TranslationsContext';

type TranslationKey = string;
type TranslationMapping = Map<TranslationKey, string | null>;

export const TranslationsProvider: FunctionComponent<PropsWithChildren> = ({ children }) => {
  const { i18n } = useTranslation();
  const { language } = i18n;
  const api = useContext(MatchingAPIContext);
  const { authInfo, resetAuth } = useContext(WidgetAuthContext);
  const { user } = useContext(UserContext);

  const [postReviewTranslations] = useState<TranslationMapping>(new Map());

  const getPostReviewTranslation = useCallback(
    async (postDocID: string) => {
      // first check if we have the translation already in the cache

      const localTranslation = postReviewTranslations.get(postDocID);
      if (localTranslation !== undefined) {
        return localTranslation;
      }

      if (!api) {
        return null;
      }

      const translationResponse = await api.translateReviewText({ post: postDocID, targetLanguage: language }, { authInfo: authInfo, resetAuth });
      let translationText: string | null = null;
      if (translationResponse) {
        const { reviewText } = translationResponse;
        if (reviewText && reviewText.length > 0) {
          translationText = reviewText;
        }
      }

      postReviewTranslations.set(postDocID, translationText);
      return translationText;
    },
    [postReviewTranslations, api, authInfo, language, resetAuth]
  );

  const translationsContextValue = useMemo(() => {
    return { getPostReviewTranslation };
  }, [getPostReviewTranslation]);

  useLayoutEffect(() => {
    updateUserDefaultUnitsIfNeeded(user, i18n.language);
  }, [i18n.language, user]);

  return <TranslationsContext.Provider value={translationsContextValue}>{children}</TranslationsContext.Provider>;
};
